/**
 * WP-V2-HO — katalog obiektów domowych (PLAN-V2 §6 House).
 * Czyste def + logika stanów (testowalna bez Phaser); scena konsumuje ObjectLayer.
 */
import type { ObjectDef, SnapZone, SpawnPlacement } from '../../interact/types.js';
import { MIN_OBJECT_SIZE } from '../../interact/types.js';

/** Świat wnętrza: 1600×720 (PLAN-V2 §4.2). */
export const HOUSE_WORLD_W = 1600;
export const HOUSE_WORLD_H = 720;

/** Etykiety PL (diakrytyki) — UI sceny, nie silnik. */
export const HOUSE_LABELS_PL: Record<string, string> = {
  house_food_bag: 'Worek karmy',
  house_bowl: 'Miska',
  house_fridge: 'Lodówka',
  house_treat: 'Smakołyk',
  house_tv: 'Telewizor',
  house_console: 'Konsola',
  house_bed: 'Łóżko',
  house_blocks_box: 'Pudełko klocków',
  house_figure_red: 'Figurka',
  house_figure_blue: 'Figurka',
  house_figure_green: 'Figurka',
  house_figure_yellow: 'Figurka',
  house_lamp: 'Lampa',
  house_window: 'Okno',
  house_cat: 'Kotek',
};

/**
 * Legacy map (statusy / lektor) — wizualnie obiekty używają proceduralnych
 * tekstur `obj_house_*` (HouseScene.ensureHouseTextures), nie emoji.
 */
export const HOUSE_ICONS: Record<string, string> = {
  house_food_bag: '🫘',
  house_bowl: '🥣',
  house_fridge: '🧊',
  house_treat: '🦴',
  house_tv: '📺',
  house_console: '🎮',
  house_bed: '🛏️',
  house_blocks_box: '📦',
  house_figure_red: '🧱',
  house_figure_blue: '🔷',
  house_figure_green: '🟢',
  house_figure_yellow: '🟡',
  house_lamp: '💡',
  house_window: '🪟',
  house_cat: '🐱',
};

/** Rodzaje proceduralnych rysunków domu (gdy brak PNG). */
export type HouseDecorKind =
  | 'food_bag'
  | 'bowl'
  | 'fridge'
  | 'treat'
  | 'tv'
  | 'console'
  | 'bed'
  | 'blocks_box'
  | 'figure_red'
  | 'figure_blue'
  | 'figure_green'
  | 'figure_yellow'
  | 'lamp'
  | 'window'
  | 'cat';

/** Mapa texture key → rodzaj rysunku (bez napisów / emoji). */
export const HOUSE_TEXTURE_SHAPES: Readonly<Record<string, HouseDecorKind>> = {
  obj_house_food_bag: 'food_bag',
  obj_house_bowl: 'bowl',
  obj_house_fridge: 'fridge',
  obj_house_treat: 'treat',
  obj_house_treat_full: 'treat',
  obj_house_treat_empty: 'treat',
  obj_house_tv: 'tv',
  obj_house_console: 'console',
  obj_house_bed: 'bed',
  obj_house_blocks_box: 'blocks_box',
  obj_house_figure_red: 'figure_red',
  obj_house_figure_blue: 'figure_blue',
  obj_house_figure_green: 'figure_green',
  obj_house_figure_yellow: 'figure_yellow',
  obj_house_lamp: 'lamp',
  obj_house_window: 'window',
  obj_house_cat: 'cat',
};

export function houseShapeForTexture(
  textureKey: string,
): HouseDecorKind | null {
  return HOUSE_TEXTURE_SHAPES[textureKey] ?? null;
}

export function houseTextureKeys(): string[] {
  return Object.keys(HOUSE_TEXTURE_SHAPES);
}

/** Id figurek drag&drop z pudełka klocków (FABUŁA „pudełko Lego"). */
export const HOUSE_FIGURE_IDS = [
  'house_figure_red',
  'house_figure_blue',
  'house_figure_green',
  'house_figure_yellow',
] as const;

export type HouseFigureId = (typeof HOUSE_FIGURE_IDS)[number];

export type BowlFill = 'empty' | 'full';
export type FridgeDoor = 'closed' | 'open';
export type PowerState = 'on' | 'off';

/** Katalog ObjectDef — wszystkie size ≥ 64. */
export function houseObjectDefs(): ObjectDef[] {
  const size = Math.max(MIN_OBJECT_SIZE, 72);
  return [
    {
      id: 'house_food_bag',
      texture: 'obj_house_food_bag',
      size,
      // drag-only (NIE 'pickup'): tap na worku nie może go chować na stałe
      // (pickupToMouth w ObjectLayer chowa obiekt bezpowrotnie i blokuje kolejne tapy) —
      // jedyna działająca ścieżka to przeciągnięcie worka na miskę (handlePourFood).
      interactions: ['drag'],
      snapTargets: ['bowl'],
      fx: 'crumbs',
    },
    {
      id: 'house_bowl',
      texture: 'obj_house_bowl',
      size,
      // tap only — scena sprawdza pełność i woła playAction('eat') + serduszka
      interactions: ['tap'],
      states: ['empty', 'full'],
      fx: 'hearts',
    },
    {
      id: 'house_fridge',
      texture: 'obj_house_fridge',
      size: Math.max(MIN_OBJECT_SIZE, 88),
      interactions: ['toggle'],
      states: ['closed', 'open'],
      fx: 'sparkle',
    },
    {
      id: 'house_treat',
      texture: 'obj_house_treat',
      size: MIN_OBJECT_SIZE,
      interactions: ['pickup', 'give', 'consume', 'drag'],
      states: ['full', 'empty'],
      fx: 'hearts',
      dogReaction: 'eat',
      snapTargets: ['bowl'],
    },
    {
      id: 'house_tv',
      texture: 'obj_house_tv',
      size: Math.max(MIN_OBJECT_SIZE, 96),
      interactions: ['toggle'],
      states: ['off', 'on'],
      fx: 'stars',
    },
    {
      id: 'house_console',
      texture: 'obj_house_console',
      size: Math.max(MIN_OBJECT_SIZE, 72),
      interactions: ['tap'],
      fx: 'sparkle',
    },
    {
      id: 'house_bed',
      texture: 'obj_house_bed',
      size: Math.max(MIN_OBJECT_SIZE, 100),
      interactions: ['tap'],
      fx: 'notes',
    },
    {
      id: 'house_blocks_box',
      texture: 'obj_house_blocks_box',
      size: Math.max(MIN_OBJECT_SIZE, 80),
      interactions: ['tap'],
      fx: 'sparkle',
    },
    {
      id: 'house_figure_red',
      texture: 'obj_house_figure_red',
      size: MIN_OBJECT_SIZE,
      interactions: ['drag'],
      snapTargets: ['sandbox'],
      fx: 'sparkle',
    },
    {
      id: 'house_figure_blue',
      texture: 'obj_house_figure_blue',
      size: MIN_OBJECT_SIZE,
      interactions: ['drag'],
      snapTargets: ['sandbox'],
      fx: 'sparkle',
    },
    {
      id: 'house_figure_green',
      texture: 'obj_house_figure_green',
      size: MIN_OBJECT_SIZE,
      interactions: ['drag'],
      snapTargets: ['sandbox'],
      fx: 'sparkle',
    },
    {
      id: 'house_figure_yellow',
      texture: 'obj_house_figure_yellow',
      size: MIN_OBJECT_SIZE,
      interactions: ['drag'],
      snapTargets: ['sandbox'],
      fx: 'sparkle',
    },
    {
      id: 'house_lamp',
      texture: 'obj_house_lamp',
      size,
      interactions: ['toggle'],
      states: ['on', 'off'],
      fx: 'sparkle',
    },
    {
      id: 'house_window',
      texture: 'obj_house_window',
      size: Math.max(MIN_OBJECT_SIZE, 84),
      interactions: ['tap'],
      fx: 'stars',
    },
    {
      id: 'house_cat',
      texture: 'obj_house_cat',
      size: MIN_OBJECT_SIZE,
      // ambient — głaskanie: serduszka + piesek macha ogonem
      interactions: ['tap'],
      fx: 'hearts',
      dogReaction: 'happy',
    },
  ];
}

/** Rozmieszczenie startowe w salonie 1600×720. */
export function housePlacements(): SpawnPlacement[] {
  return [
    { defId: 'house_window', x: 180, y: 220 },
    { defId: 'house_lamp', x: 320, y: 300 },
    { defId: 'house_bed', x: 280, y: 520 },
    { defId: 'house_fridge', x: 520, y: 340 },
    { defId: 'house_food_bag', x: 640, y: 480 },
    { defId: 'house_bowl', x: 760, y: 520 },
    { defId: 'house_tv', x: 1100, y: 300 },
    { defId: 'house_console', x: 1220, y: 380 },
    { defId: 'house_blocks_box', x: 1400, y: 500 },
    { defId: 'house_figure_red', x: 1340, y: 560 },
    { defId: 'house_figure_blue', x: 1400, y: 580 },
    { defId: 'house_figure_green', x: 1460, y: 560 },
    { defId: 'house_figure_yellow', x: 1420, y: 620 },
    { defId: 'house_cat', x: 900, y: 400 },
  ];
}

export function houseSnapZones(): SnapZone[] {
  return [
    { id: 'bowl', x: 760, y: 520, radius: 56 },
    { id: 'sandbox', x: 1400, y: 560, radius: 90 },
  ];
}

/** Lista assetów PNG oczekiwanych (placeholdery do czasu artu). */
export function houseMissingAssetKeys(): string[] {
  const keys = new Set<string>();
  for (const d of houseObjectDefs()) {
    keys.add(d.texture);
    if (d.states) {
      for (const s of d.states) {
        keys.add(`obj_${d.id}_${s}`);
      }
    }
  }
  // smakołyki z lodówki — te same tekstury house_treat
  keys.add('obj_house_treat_full');
  keys.add('obj_house_treat_empty');
  return [...keys].sort();
}

// ─── Czysta logika (E2E / unit) ─────────────────────────────────────────────

/** Nasyp karmę z worka do miski (snap worka na miskę). */
export function pourFoodIntoBowl(bowl: BowlFill): BowlFill {
  return 'full';
}

/** Pies je z miski — tylko gdy pełna. */
export function feedFromBowl(bowl: BowlFill): { bowl: BowlFill; ate: boolean; action: 'eat' | null } {
  if (bowl !== 'full') {
    return { bowl, ate: false, action: null };
  }
  return { bowl: 'empty', ate: true, action: 'eat' };
}

/** Toggle TV — zwraca nowy stan. */
export function toggleTv(current: PowerState): PowerState {
  return current === 'on' ? 'off' : 'on';
}

/** Toggle lampy. */
export function toggleLamp(current: PowerState): PowerState {
  return current === 'on' ? 'off' : 'on';
}

/**
 * Otwarcie lodówki: przy pierwszym open zwraca 3 smakołyki do spawnu.
 * Kolejne open bez respawnu, dopóki `treatsSpawned`.
 */
export function fridgeTreatSpawnCount(
  door: FridgeDoor,
  treatsSpawned: boolean,
  treatCount = 3,
): number {
  if (door === 'open' && !treatsSpawned) return treatCount;
  return 0;
}

export function advanceFridgeDoor(current: FridgeDoor): FridgeDoor {
  return current === 'closed' ? 'open' : 'closed';
}

/** Łóżko → sen psa. */
export function bedAction(defId: string): 'sleep' | null {
  return defId === 'house_bed' ? 'sleep' : null;
}

/** Czy def to figurka z piaskownicy. */
export function isHouseFigure(defId: string): boolean {
  return (HOUSE_FIGURE_IDS as readonly string[]).includes(defId);
}

/** Drag figurek dozwolony. */
export function canDragFigure(defId: string): boolean {
  return isHouseFigure(defId);
}

/** Konsola → hub mini-gier. */
export function isConsoleEntry(defId: string): boolean {
  return defId === 'house_console';
}

/** Okno → ptaszek. */
export function isWindow(defId: string): boolean {
  return defId === 'house_window';
}

/** Czy katalog ma ≥8 interaktywnych typów (DoD §6). */
export function countInteractiveKinds(defs: ObjectDef[] = houseObjectDefs()): number {
  return defs.filter((d) => d.interactions.length > 0).length;
}

/** Walidacja: każdy def size ≥ MIN_OBJECT_SIZE. */
export function allDefsMeetMinTouch(defs: ObjectDef[] = houseObjectDefs()): boolean {
  return defs.every((d) => d.size >= MIN_OBJECT_SIZE);
}

/**
 * Pozycje 3 smakołyków przy lodówce (offsety lokalne).
 */
export function treatSpawnOffsets(): Array<{ dx: number; dy: number }> {
  return [
    { dx: -50, dy: 40 },
    { dx: 0, dy: 55 },
    { dx: 50, dy: 40 },
  ];
}
